import { z } from 'zod';
import {
  avatarColorSchema,
  currencySchema,
  displayNameSchema,
  emojiSchema,
  idSchema,
  isoDateTimeSchema,
  localeSchema,
  themeSchema,
  timezoneSchema,
} from './common';

export const roleSchema = z.enum(['admin', 'member']);
export type Role = z.infer<typeof roleSchema>;

export const userStatusSchema = z.enum(['active', 'disabled']);
export type UserStatus = z.infer<typeof userStatusSchema>;

/**
 * Payment handles, already normalised by the server with `@split/core`:
 * PayPal.me name, Revolut username (lower case), Bizum phone in E.164 (`+34612345678`).
 * Build links with `paypalMeUrl` / `revolutMeUrl` / `bizumInfo` from `@split/core`.
 */
export const paymentHandlesSchema = z.object({
  paypal: z.string().nullable(),
  revolut: z.string().nullable(),
  bizumPhone: z.string().nullable(),
});
export type PaymentHandles = z.infer<typeof paymentHandlesSchema>;

/** What other people see about a user. */
export const userPublicSchema = z.object({
  id: idSchema,
  displayName: z.string(),
  avatarEmoji: z.string().nullable(),
  avatarColor: z.int().nullable(),
  disabled: z.boolean(),
});
export type UserPublic = z.infer<typeof userPublicSchema>;

/** The signed-in user's own profile (`GET /api/me`). */
export const meSchema = userPublicSchema.extend({
  role: roleSchema,
  /** Optional, stored only (no verification flow in v1). */
  email: z.string().nullable(),
  /** Optional, stored only (no verification flow in v1). */
  phone: z.string().nullable(),
  paymentHandles: paymentHandlesSchema,
  locale: localeSchema,
  theme: themeSchema,
  defaultCurrency: z.string(),
  timezone: z.string(),
  hasPasskey: z.boolean(),
  createdAt: isoDateTimeSchema,
});
export type Me = z.infer<typeof meSchema>;

const nullableTrimmed = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullable()
    .transform((v) => (v === '' ? null : v));

/**
 * `PATCH /api/me`. Every field is optional; `null` (or `""`) clears an optional field.
 * Handles accept pasted links (`https://paypal.me/name`, `@name`); the server normalises them
 * and answers `VALIDATION_FAILED` if they cannot be normalised.
 */
export const updateMeRequestSchema = z
  .object({
    displayName: displayNameSchema,
    avatarEmoji: emojiSchema.nullable(),
    avatarColor: avatarColorSchema.nullable(),
    email: z
      .union([z.email().max(254), z.literal('')])
      .nullable()
      .transform((v) => (v === '' ? null : v)),
    phone: nullableTrimmed(32),
    paypal: nullableTrimmed(100),
    revolut: nullableTrimmed(100),
    bizumPhone: nullableTrimmed(32),
    locale: localeSchema,
    theme: themeSchema,
    defaultCurrency: currencySchema,
    timezone: timezoneSchema,
  })
  .partial();
export type UpdateMeRequest = z.input<typeof updateMeRequestSchema>;

/** `GET /api/users/:id`: public profile, with payment handles when you share a group. */
export const userProfileSchema = userPublicSchema.extend({
  paymentHandles: paymentHandlesSchema.nullable(),
  /** The user's default currency (e.g. to offer it when settling up). */
  defaultCurrency: z.string(),
});
export type UserProfile = z.infer<typeof userProfileSchema>;
